'use client';
import { useMemo, useState } from 'react';
import { useResource, invalidateResource } from '@/lib/use-resource';
import { PriceChart } from '@/components/stock-detail/PriceChart';
import { HoldingsList } from './HoldingsList';
import { StockDetail } from '@/components/StockDetail';
import { StockStoryDrawer } from '@/components/StockStoryDrawer';
import type { PricePoint } from '@/lib/stock-detail-helpers';
import type { ScoredStock } from '@/lib/compute';

interface TargetPricesResp {
  stocks?: Array<{ symbol: string; currentPrice: number | null; threeYearTarget: number | null }>;
}

interface PerStock {
  symbol: string;
  name: string | null;
  sector: string | null;
  rank: number | null;
  anchorPrice: number;
  currentPrice: number | null;
  pctReturn: number | null;
  enteredOn: string | null;
  daysIn: number | null;
  pctToday: number | null;
  pctMtd: number | null;
  pctYtd: number | null;
  pctSinceAddition: number | null;
  weight: number | null;
}

interface TrackerResponse {
  anchorDate: string;
  mode: 'strict' | 'whatif';
  asOfPricesUpdatedAt: string | null;
  asOfDate: string;
  basketSize: number;
  perStock: PerStock[];
  summary: {
    alphaReturn: number;
    alphaReturnMedian: number;
    hitRate: number;
    spyAnchor: number | null;
    spyCurrent: number | null;
    spyReturn: number | null;
    edge: number | null;
    edgeMedian: number | null;
    coverage: number;
    basketPctToday: number | null;
    basketPctMtd: number | null;
    basketPctYtd: number | null;
    basketPctSinceAddition: number | null;
    avgDaysIn: number | null;
  };
  notice: string | null;
  error?: string;
}

// Anchor selector retired 2026-05-05: the canonical alpha-basket file owns
// the inception date. Switching anchors mid-flight created the "+2.75% since
// May 1" vs "+5.2% MTD" mismatch.

const fmtPct = (v: number, decimals = 2) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(decimals)}%`;
const fmtPp = (v: number, decimals = 2) => `${v >= 0 ? '+' : ''}${(v * 100).toFixed(decimals)}pp`;

/**
 * Live tracker for the Alpha Zone basket. Strict-anchor mode reads the
 * snapshot at data/live-tracker/snapshots/{anchor}.json (basket frozen at
 * anchor date). What-if mode retro-prices today's basket — useful for
 * direction-of-edge but biased because we picked the names knowing how they
 * performed since the anchor.
 */
/**
 * Slot prop lets the page render the Tracker as two distinct sections —
 * 'headline' for the performance card at the top, 'holdings' for the
 * per-position table + chart further down. Default 'both' keeps the
 * legacy single-block render for any consumer that hasn't migrated.
 */
export function AlphaZoneTracker({ slot = 'both' }: { slot?: 'both' | 'headline' | 'holdings' } = {}) {
  const [refreshing, setRefreshing] = useState(false);
  // Click a row in the per-position table → render its price chart below.
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null);
  // Single canonical endpoint — no per-anchor URLs. The basket's inception
  // date IS the anchor, owned by data/alpha-basket.json.
  const trackerUrl = `/api/alpha-zone-tracker`;
  const { data, loading } = useResource<TrackerResponse>(trackerUrl);
  // Price history for the selected symbol — fetched only when a row is clicked.
  const { data: pricesRaw } = useResource<{ prices?: PricePoint[] }>(
    selectedSymbol ? `/api/prices?symbol=${selectedSymbol}` : null,
  );
  const selectedPrices = pricesRaw?.prices ?? [];
  // Per-stock 3-year targets (from compute-target-prices.ts). Switched from 5y
  // → 3y on 2026-05-19 — anchored to real BBG 3y consensus EPS, not extrapolated.
  const { data: targetPrices } = useResource<TargetPricesResp>('/api/target-prices');
  // Full stock records (for StockDetail one-pager when a holding is clicked).
  const { data: stocksResp } = useResource<{ stocks: ScoredStock[] }>('/api/stocks');
  const allStocks: ScoredStock[] = stocksResp?.stocks ?? [];
  const selectedStock: ScoredStock | null = selectedSymbol
    ? allStocks.find(s => s.symbol === selectedSymbol) ?? null
    : null;

  // 5-year expected return on the basket: Σ over members of weight ×
  // (fiveYearTarget/currentPrice − 1). Members without a target (banks,
  // missing forward EPS) contribute 0 — typical coverage is logged below.
  const fiveYr = useMemo(() => {
    const stocks = targetPrices?.stocks;
    if (!data?.perStock?.length || !stocks) return { cumul: null as number | null, annualized: null as number | null };
    const tpBySymbol = new Map<string, { current: number | null; target: number | null }>();
    for (const r of stocks) tpBySymbol.set(r.symbol, { current: r.currentPrice, target: r.threeYearTarget });
    let totalW = 0, coveredW = 0, cum = 0;
    for (const s of data.perStock) {
      const w = s.weight ?? 1 / data.perStock.length;
      totalW += w;
      const tp = tpBySymbol.get(s.symbol);
      if (!tp || tp.current == null || tp.target == null || tp.current <= 0) continue;
      const r = tp.target / tp.current - 1;
      if (!Number.isFinite(r)) continue;
      cum += w * r;
      coveredW += w;
    }
    if (coveredW <= 0 || totalW <= 0) return { cumul: null, annualized: null };
    // Renormalize over covered weight so the basket-level number reflects the
    // expected return on the *covered* portion of the basket, then scale back.
    // For a basket with 100% covered, this is a no-op. For partial coverage
    // it implicitly assumes uncovered names earn the same as covered ones.
    const cumulNorm = cum / coveredW;
    const annualized = Math.pow(1 + cumulNorm, 1 / 5) - 1;
    return { cumul: cumulNorm, annualized };
  }, [data, targetPrices]);

  const handleRefreshPrices = async () => {
    setRefreshing(true);
    try {
      await fetch('/api/refresh-prices', { method: 'POST' });
      // refresh-prices.ts rewrites scores.json AND the prices snapshot.
      // Every surface that reads either through useResource needs its
      // cache dropped — otherwise the holdings table keeps showing weeks-
      // old prices while the headline updates.
      invalidateResource(trackerUrl);
      invalidateResource('/api/stocks');
      invalidateResource('/api/holdings-pnl');
      invalidateResource('/api/portfolio-snapshot');
      invalidateResource('/api/alpha-basket');
      invalidateResource('/api/portfolio');
      // Force every component to re-render with the dropped caches.
      window.location.reload();
    } finally {
      setRefreshing(false);
    }
  };

  if (loading && !data) return <div className="text-[12px] text-[#64748b]">Loading tracker…</div>;
  if (!data || data.error) {
    return <div className="text-[12px] text-[#64748b]">{data?.error ?? 'Tracker unavailable.'}</div>;
  }

  const s = data.summary;
  const isWhatIf = data.mode === 'whatif';

  const showHeadline = slot === 'both' || slot === 'headline';
  const showHoldings = slot === 'both' || slot === 'holdings';

  return (
    <div className="space-y-5">
      {/* Headline */}
      {showHeadline && (
      <div className={`bg-[#0d1117] border ${isWhatIf ? 'border-orange-500/40' : 'border-amber-500/40'} rounded-lg px-3 py-2`}>
        {isWhatIf && data.notice && (
          <div className="mb-2 px-2 py-1 rounded border border-orange-500/30 bg-orange-500/5 text-[10px] text-orange-300 leading-snug">
            <span className="font-bold uppercase tracking-wide mr-1">Bias warning:</span>
            {data.notice}
          </div>
        )}
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-y-2">
            <FlatMetric label="Basket today" value={s.basketPctToday} />
            <FlatMetric label="Basket MTD" value={s.basketPctMtd} />
            <FlatMetric label="Basket YTD" value={s.basketPctYtd} />
            <FlatMetric label="Basket since incept" value={s.alphaReturn} />
            <FlatMetric label="SPY since incept" value={s.spyReturn ?? null} neutral />
            <FlatMetric label="Basket − SPY" value={s.edge ?? null} unit="pp" />
            <FlatMetric label="Hit rate" value={s.hitRate} />
            <FlatMetric label="Avg days in" value={s.avgDaysIn} unit="n" />
            <FlatMetric label="5y target" value={fiveYr.cumul} />
            <FlatMetric label="5y target · ann" value={fiveYr.annualized} />
          </div>
          <button
            type="button"
            onClick={handleRefreshPrices}
            disabled={refreshing}
            className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border border-[#334155] text-[#94a3b8] hover:text-white hover:border-amber-500/50 disabled:opacity-50"
          >
            {refreshing ? 'refreshing…' : 'refresh prices'}
          </button>
        </div>
        <div className="text-[9px] text-[#64748b] mt-1">
          {data.basketSize} stocks · inception {data.anchorDate} · live · {data.asOfPricesUpdatedAt ? new Date(data.asOfPricesUpdatedAt).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'no timestamp'}
          {isWhatIf && <> · <strong className="text-orange-300">median diagnostic only</strong></>}
        </div>
      </div>
      )}

      {/* Per-stock breakdown — basket members rendered through the same
          HoldingsList component used on /portfolio so the two pages look
          identical. Steve directive 2026-05-09: "EXACTLY THE SAME LOOKING PAGES".
          Equal-weight; alpha-zone-leg specifics (entry date, since-entry %)
          are surfaced in the headline card above. */}
      {showHoldings && (
      <HoldingsList
        symbols={data.perStock.map((p) => p.symbol)}
        title="Alpha Zone Holdings"
        readOnly
        onSelectSymbol={setSelectedSymbol}
      />
      )}

      {/* Selected stock's price chart — fetched on click. */}
      {showHoldings && selectedSymbol && (
        <div className="bg-[#0d1117] border border-amber-500/30 rounded-lg p-4">
          <div className="flex items-baseline justify-between mb-3">
            <div className="text-[12px] uppercase tracking-wide text-[#94a3b8] font-semibold">
              <span className="font-mono text-amber-300 text-[14px] mr-2">{selectedSymbol}</span>
              price chart
            </div>
            <button
              type="button"
              onClick={() => setSelectedSymbol(null)}
              className="text-[10px] uppercase tracking-wide text-[#64748b] hover:text-white"
            >
              close
            </button>
          </div>
          {selectedPrices.length > 0
            ? <PriceChart allPrices={selectedPrices} />
            : <div className="text-[11px] text-[#64748b] py-8 text-center">Loading prices…</div>}
        </div>
      )}

      {/* Alpha Zone alerts moved into the AlphaZoneVisualizer's bottom-left
          corner overlay (mounted above this component on the Research Desk
          alpha-zone tab) so the 3D scene + alerts share visual real estate. */}

      {/* Click-on-symbol → unified two-pager: StockDetail (left half) +
          StockStoryDrawer (right half), shared backdrop, single close.
          Matches /portfolio behavior so the alpha-zone holdings table opens
          the same surface as the portfolio holdings table. */}
      {selectedSymbol && (
        <>
          <div className="fixed inset-0 bg-black/70 z-40" onClick={() => setSelectedSymbol(null)} />
          {selectedStock && (
            <StockDetail
              stock={selectedStock}
              allStocks={allStocks}
              pinnedSymbols={new Set()}
              onTogglePin={() => {}}
              onClose={() => setSelectedSymbol(null)}
              noBackdrop
              isAlphaZone
            />
          )}
          <StockStoryDrawer symbol={selectedSymbol} onClose={() => setSelectedSymbol(null)} />
        </>
      )}
    </div>
  );
}

function FlatMetric({ label, value, unit = '%', neutral = false }: {
  label: string;
  value: number | null;
  unit?: '%' | 'pp' | 'n';
  neutral?: boolean;
}) {
  const display = value == null
    ? '—'
    : unit === 'n'
      ? `${Math.round(value)}d`
      : unit === 'pp'
        ? fmtPp(value)
        : fmtPct(value);
  const tone = value == null ? 'text-[#64748b]'
    : unit === 'n' || neutral ? 'text-[#cbd5e1]'
    : value > 0 ? 'text-emerald-400'
    : value < 0 ? 'text-red-400'
    : 'text-[#cbd5e1]';
  return (
    <div className="flex flex-col leading-tight">
      <span className="text-[9px] uppercase tracking-wide text-[#64748b] whitespace-nowrap">{label}</span>
      <span className={`text-[12px] font-bold tabular-nums ${tone}`}>{display}</span>
    </div>
  );
}

